const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

export const LOGIN_VERIFIED_PATH = '/login?verified=true';
export const LOGIN_RESET_PATH = '/login?reset=true';

export function buildEmailVerificationActionCodeSettings(origin: string): {
  url: string;
  handleCodeInApp: false;
} {
  return {
    url: `${origin}${LOGIN_VERIFIED_PATH}`,
    handleCodeInApp: false,
  };
}

export function isEmailActionLoginHold(searchParams: { get: (key: string) => string | null }): boolean {
  return searchParams.get('verified') === 'true' || searchParams.get('reset') === 'true';
}

export function getLoginEmailActionNotice(searchParams: { get: (key: string) => string | null }): string {
  if (searchParams.get('verified') === 'true') {
    return 'Email verified. Sign in to continue.';
  }
  if (searchParams.get('reset') === 'true') {
    return 'Password updated. Sign in with your new password.';
  }
  return '';
}

export function resolveSameOriginContinuePath(continueUrl: string | null | undefined, origin: string): string | null {
  if (!continueUrl) return null;
  try {
    const parsed = new URL(continueUrl);
    if (parsed.origin !== origin) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function resolvePostVerifyLoginPath(
  continueUrl: string | null | undefined,
  origin: string,
  email?: string | null,
): string {
  const sameOriginPath = resolveSameOriginContinuePath(continueUrl, origin);
  const basePath = sameOriginPath && sameOriginPath.startsWith('/login')
    ? sameOriginPath
    : LOGIN_VERIFIED_PATH;

  if (!email) return ensureVerifiedFlag(basePath);

  const url = new URL(basePath, origin);
  if (!url.searchParams.get('verified')) {
    url.searchParams.set('verified', 'true');
  }
  url.searchParams.set('email', email);
  return `${url.pathname}?${url.searchParams.toString()}`;
}

function ensureVerifiedFlag(path: string): string {
  if (path.includes('verified=')) return path;
  return path.includes('?') ? `${path}&verified=true` : `${path}?verified=true`;
}

export function mapAuthActionErrorMessage(
  code: string | undefined,
  kind: 'verify' | 'reset' | 'generic',
): string {
  if (code === 'auth/expired-action-code') {
    return kind === 'reset'
      ? 'This password reset link has expired. Please request a new one from the sign-in page.'
      : 'This verification link has expired. Please request a new verification email.';
  }
  if (code === 'auth/invalid-action-code') {
    return kind === 'reset'
      ? 'This password reset link is invalid or has already been used. Please request a new one from the sign-in page.'
      : 'This verification link is invalid or has already been used. Please request a new verification email.';
  }
  if (code === 'auth/user-disabled') {
    return 'This account has been disabled. Please contact support for assistance.';
  }
  if (code === 'auth/weak-password') {
    return 'Password is too weak. Please use a stronger password.';
  }
  if (kind === 'reset') {
    return 'Password reset failed. Please try again or contact support if the problem persists.';
  }
  if (kind === 'verify') {
    return 'Verification failed. Please try again or contact support if the problem persists.';
  }
  return 'This email link is not valid. Please use the latest link from your inbox, or contact support.';
}

export type AuthActionAuth = {
  currentUser: unknown;
};

export async function completeVerifyEmailAction<TAuth extends AuthActionAuth>(input: {
  oobCode: string;
  continueUrl: string | null;
  origin: string;
  auth: TAuth;
  checkActionCode: (auth: TAuth, oobCode: string) => Promise<{ data?: { email?: string | null } }>;
  applyActionCode: (auth: TAuth, oobCode: string) => Promise<void>;
  signOutUser: (reason: string) => Promise<void>;
}): Promise<string> {
  const info = await input.checkActionCode(input.auth, input.oobCode);
  await input.applyActionCode(input.auth, input.oobCode);
  if (input.auth.currentUser) {
    try {
      await input.signOutUser('email-verification');
    } catch {
      // Login also signs out when verified=true; do not block the redirect.
    }
  }
  return resolvePostVerifyLoginPath(input.continueUrl, input.origin, info?.data?.email);
}

export async function completePasswordReset<TAuth extends AuthActionAuth>(input: {
  oobCode: string;
  password: string;
  auth: TAuth;
  confirmPasswordReset: (auth: TAuth, oobCode: string, password: string) => Promise<void>;
  signOutUser: (reason: string) => Promise<void>;
}): Promise<string> {
  await input.confirmPasswordReset(input.auth, input.oobCode, input.password);
  if (input.auth.currentUser) {
    try {
      await input.signOutUser('password-reset');
    } catch {
      // Login also signs out when reset=true; do not block the redirect.
    }
  }
  return LOGIN_RESET_PATH;
}

export async function inspectResetPasswordCode<TAuth extends AuthActionAuth>(input: {
  oobCode: string;
  auth: TAuth;
  checkActionCode: (auth: TAuth, oobCode: string) => Promise<unknown>;
}): Promise<void> {
  await input.checkActionCode(input.auth, input.oobCode);
}

export function validateResetPassword(password: string, confirmPassword: string): {
  password: string;
  confirmPassword: string;
} {
  let passwordError = '';
  if (!password) passwordError = 'Password is required';
  else if (password.length < 8) passwordError = 'Password must be at least 8 characters';
  else if (!passwordPattern.test(password)) {
    passwordError = 'Password must contain uppercase, lowercase, number, and special character';
  }

  let confirmPasswordError = '';
  if (!confirmPassword) confirmPasswordError = 'Please confirm your password';
  else if (confirmPassword !== password) confirmPasswordError = 'Passwords do not match';

  return { password: passwordError, confirmPassword: confirmPasswordError };
}
